import type * as hast from 'hast';

/**
 * Returns true if the given Node is text and only whitespace.
 */
export function isWhitespace(
    node: hast.Element | hast.ElementContent | hast.Comment | hast.Text
): boolean {
    return (
        node.type === 'text' &&
        typeof node.value === 'string' &&
        sanitizeSpaces(node.value).trim() === ''
    );
}

/**
 * sanitizeSpaces replace non-breaking spaces with regular space
 * non-breaking spaces (aka &nbsp;) are sources of many problems & quirks
 * &nbsp; in ascii is `0xA0` or `0xC2A0` in utf8
 *
 * Also remove any trailing new lines.
 */
export function sanitizeSpaces(str: string): string {
    return str.replace(/\xa0/g, ' ').replace(/(\t|\n|\r)$/gi, '');
}

/**
 * Trim whitespace, including new lines and breaks.
 */
export function trimWhitespace(str: string): string {
    return trimNewlines(str).trim();
}

/**
 * Trim leading and trailing new lines.
 */
export function trimNewlines(str: string): string {
    return str.replace(/(\n|\r)$/gi, '').replace(/^(\n|\r)/gi, '');
}

/**
 * Concatenate the text of the current Node and all children.
 */
export function concatText(node: hast.Element | hast.Element[], input = '') {
    return (Array.isArray(node) ? node : node.children).reduce((t, child) => {
        if (child.type === 'text') {
            return t + child.value;
        }

        // Ignore text from comments
        if (child.type === 'comment') {
            return t;
        }

        // @ts-expect-error
        return concatText(child, t);
    }, input);
}
